import { themeAtom } from "../../../shared/atoms";
import DataGridToolbar from "../../../shared/components/DataGridToolbar";
import { toastError, toastSuccess } from "../../../shared/components/ToastContainer";
import { logE } from "../../../shared/services/debug-logger";
import {
  AdminPaintSchemeFormValue,
  AdminPaintSchemeGridRow,
  PAINT_PERFORMANCE_INDEXES,
} from "../../models/admin-paint-scheme.models";
import {
  getAdminPaintSchemeErrorMessage,
  getAdminPaintSchemes,
  createAdminPaintScheme,
  deleteAdminPaintScheme,
  updateAdminPaintScheme,
} from "../../services/admin-paint-scheme.service";
import {
  createAdminPaintSchemeCsv,
  formatAdminPaintSchemeUploadTimestamp,
  getAdminPaintSchemeDisplayLabel,
  toAdminPaintSchemeGridRows,
  toAdminPaintSchemeRecordFromForm,
} from "../../utils/admin-paint-scheme.utils";
import AdminPaintSchemeModal from "./AdminPaintSchemeModal";
import PerformanceIndexLegend from "./PerformanceIndexLegend";
import { getPerformanceIndexColorClasses } from "./performance-index.styles";
import { Dialog, Menu, Transition } from "@headlessui/react";
import {
  ArrowsUpDownIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

type LoadState = "loading" | "ready" | "error";
type Mutation = "add" | "edit" | "delete" | null;
type DialogMode = Exclude<Mutation, null>;
type SortKey = "VENDOR" | "PAINT" | "PERFORMANCE_INDEX" | "HEMPEL_TIER" | "COMMENTS" | "UPLOAD_TS";

const performanceFilterOptions = PAINT_PERFORMANCE_INDEXES.map((index) => ({ value: index, label: index }));

const AdminPaintSchemePanel = () => {
  const theme = useAtomValue(themeAtom);
  const [rows, setRows] = useState<AdminPaintSchemeGridRow[]>([]);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [mutation, setMutation] = useState<Mutation>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogMode | null>(null);
  const [selectedRow, setSelectedRow] = useState<AdminPaintSchemeGridRow | null>(null);
  const [search, setSearch] = useState("");
  const [performanceFilter, setPerformanceFilter] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("VENDOR");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const loadPaintSchemes = useCallback(async () => {
    setLoadState("loading");
    setErrorMessage(null);
    try {
      const response = await getAdminPaintSchemes();
      setRows(toAdminPaintSchemeGridRows(response.rows ?? []));
      setLoadState("ready");
    } catch (error) {
      logE(error);
      setErrorMessage(getAdminPaintSchemeErrorMessage(error, "Unable to load Paints Scheme records."));
      setLoadState("error");
    }
  }, []);

  useEffect(() => {
    void loadPaintSchemes();
  }, [loadPaintSchemes]);

  const visibleRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const filtered = rows.filter(
      (row) =>
        (performanceFilter === "all" || row.PERFORMANCE_INDEX === performanceFilter) &&
        (!normalizedSearch ||
          [row.VENDOR, row.PAINT, row.PERFORMANCE_INDEX, row.HEMPEL_TIER, row.COMMENTS, row.UPLOAD_TS].some((value) =>
            String(value ?? "")
              .toLowerCase()
              .includes(normalizedSearch),
          )),
    );

    return [...filtered].sort((left, right) => {
      const comparison = String(left[sortKey] ?? "").localeCompare(String(right[sortKey] ?? ""), undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [performanceFilter, rows, search, sortDirection, sortKey]);

  const isMutating = mutation !== null;

  const toggleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDirection("asc");
      }
    },
    [sortKey],
  );

  const exportPaintSchemes = useCallback(() => {
    const csv = createAdminPaintSchemeCsv(visibleRows);
    const downloadUrl = URL.createObjectURL(
      new Blob([String.fromCharCode(0xfeff), csv], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = "paints-scheme-" + new Date().toISOString().slice(0, 10) + ".csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(downloadUrl);
  }, [visibleRows]);

  const addPaint = useCallback(
    async (form: AdminPaintSchemeFormValue) => {
      setDialogError(null);
      try {
        const record = toAdminPaintSchemeRecordFromForm(form);
        setMutation("add");
        await createAdminPaintScheme(record);
        await loadPaintSchemes();
        setDialog(null);
        toastSuccess("Paint added to the Paints Scheme table.");
      } catch (error) {
        const message = getAdminPaintSchemeErrorMessage(error, "Unable to add the paint.");
        setDialogError(message);
        toastError(message);
      } finally {
        setMutation(null);
      }
    },
    [loadPaintSchemes],
  );

  const editPaint = useCallback(
    async (form: AdminPaintSchemeFormValue) => {
      if (!selectedRow) return;
      setDialogError(null);
      try {
        const record = toAdminPaintSchemeRecordFromForm(form);
        setMutation("edit");
        await updateAdminPaintScheme(selectedRow.VENDOR, selectedRow.PAINT, record);
        await loadPaintSchemes();
        setDialog(null);
        setSelectedRow(null);
        toastSuccess("Paint Scheme record updated.");
      } catch (error) {
        const message = getAdminPaintSchemeErrorMessage(error, "Unable to update the paint.");
        setDialogError(message);
        toastError(message);
      } finally {
        setMutation(null);
      }
    },
    [loadPaintSchemes, selectedRow],
  );

  const deletePaint = useCallback(async () => {
    if (!selectedRow) return;
    setMutation("delete");
    try {
      await deleteAdminPaintScheme(selectedRow.VENDOR, selectedRow.PAINT);
      await loadPaintSchemes();
      setDialog(null);
      setSelectedRow(null);
      toastSuccess("Paint Scheme record deleted.");
    } catch (error) {
      const message = getAdminPaintSchemeErrorMessage(error, "Unable to delete the paint.");
      setErrorMessage(message);
      toastError(message);
    } finally {
      setMutation(null);
    }
  }, [loadPaintSchemes, selectedRow]);

  return (
    <main
      data-test="admin-paint-schemes-page"
      className={twMerge("flex min-h-0 flex-1 flex-col overflow-hidden p-6", theme.home.tailwind.textDefault)}
    >
      <section
        className={twMerge(
          "flex min-h-0 flex-1 flex-col rounded-lg p-6 shadow-md",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
        )}
      >
        <div className="border-b pb-4">
          <div>
            <h1 className="text-base font-bold text-green">Paints Scheme</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Maintain the paint combinations used by vessel Dry Dock records.
            </p>
          </div>
        </div>

        <div className="ml-auto mt-4 w-full max-w-xl">
          <PerformanceIndexLegend />
        </div>

        {errorMessage && (
          <div
            role="alert"
            className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
          >
            {errorMessage}
          </div>
        )}

        {loadState === "loading" ? (
          <LoadingState />
        ) : loadState === "error" ? (
          <ErrorState onRetry={() => void loadPaintSchemes()} />
        ) : (
          <div className="mt-5 flex min-h-0 flex-1 flex-col gap-3">
            <DataGridToolbar
              searchValue={search}
              onSearchChange={setSearch}
              searchAriaLabel="Search Paints Scheme records"
              filterValue={performanceFilter}
              onFilterChange={setPerformanceFilter}
              filterAriaLabel="Filter paints by performance index"
              allFilterLabel="All performance indexes"
              filterOptions={performanceFilterOptions}
              resultCount={visibleRows.length}
              totalCount={rows.length}
              resultLabel="paints"
              onReset={() => {
                setSearch("");
                setPerformanceFilter("all");
                setSortKey("VENDOR");
                setSortDirection("asc");
              }}
              resetAriaLabel="Reset Paints Scheme grid"
              onExport={exportPaintSchemes}
              exportLabel="Export Paints Scheme to CSV"
              exportDisabled={visibleRows.length === 0}
              onAdd={() => {
                setSelectedRow(null);
                setDialogError(null);
                setDialog("add");
              }}
              addLabel="Add a Paint"
              addDisabled={isMutating}
            />

            <PaintSchemeTable
              rows={visibleRows}
              sortKey={sortKey}
              sortDirection={sortDirection}
              disabled={isMutating}
              onSort={toggleSort}
              onEdit={(row) => {
                setSelectedRow(row);
                setDialogError(null);
                setDialog("edit");
              }}
              onDelete={(row) => {
                setSelectedRow(row);
                setDialog("delete");
              }}
            />
          </div>
        )}
      </section>

      <AdminPaintSchemeModal
        open={dialog === "add" || dialog === "edit"}
        record={dialog === "edit" ? selectedRow : null}
        saving={mutation === "add" || mutation === "edit"}
        errorMessage={dialogError}
        onCancel={() => !isMutating && setDialog(null)}
        onSave={dialog === "edit" ? editPaint : addPaint}
      />
      <DeletePaintSchemeDialog
        open={dialog === "delete"}
        row={selectedRow}
        deleting={mutation === "delete"}
        onCancel={() => !isMutating && setDialog(null)}
        onConfirm={() => void deletePaint()}
      />
    </main>
  );
};

function PaintSchemeTable({
  rows,
  sortKey,
  sortDirection,
  disabled,
  onSort,
  onEdit,
  onDelete,
}: {
  rows: AdminPaintSchemeGridRow[];
  sortKey: SortKey;
  sortDirection: "asc" | "desc";
  disabled: boolean;
  onSort: (key: SortKey) => void;
  onEdit: (row: AdminPaintSchemeGridRow) => void;
  onDelete: (row: AdminPaintSchemeGridRow) => void;
}) {
  const theme = useAtomValue(themeAtom);
  return (
    <div
      data-test="admin-paint-schemes-grid"
      className={twMerge(
        "min-h-0 flex-1 overflow-auto rounded-lg border [scrollbar-color:#B7C9C4_#F3F4F4] dark:[scrollbar-color:#4B5563_#1F2937] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-[#F3F4F4] dark:[&::-webkit-scrollbar-track]:bg-gray-800 [&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar]:w-2.5",
        theme.myVesselsTable.tailwind.tableBorder,
        theme.uwhcAssistant.tableScroll,
        theme.uwhcAssistant.scrolHover,
      )}
    >
      <table className={twMerge("min-w-full text-[12px]", theme.vesselDetails.info)}>
        <thead className="sticky top-0 z-10 text-[#2F7F7B] dark:text-teal-300">
          <tr
            className={twMerge(
              "border-b",
              theme.myVesselsTable.tailwind.tableBorder,
              theme.uwhcAssistant.chatUi.headerBg,
              theme.uwhcAssistant.chatUi.panelBorder,
            )}
          >
            <SortableHeader label="Vendor" column="VENDOR" {...{ sortKey, sortDirection, onSort }} />
            <SortableHeader label="Paint" column="PAINT" {...{ sortKey, sortDirection, onSort }} />
            <SortableHeader
              label="Performance Index"
              column="PERFORMANCE_INDEX"
              {...{ sortKey, sortDirection, onSort }}
            />
            <SortableHeader label="Hempel Tier" column="HEMPEL_TIER" {...{ sortKey, sortDirection, onSort }} />
            <SortableHeader label="Comments" column="COMMENTS" {...{ sortKey, sortDirection, onSort }} />
            <SortableHeader label="Uploaded (UTC)" column="UPLOAD_TS" {...{ sortKey, sortDirection, onSort }} />
            <th
              className={twMerge(
                "sticky right-0 z-20 w-20 border-l px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.45)]",
                theme.myVesselsTable.tailwind.tableBorder,
                theme.uwhcAssistant.chatUi.headerBg,
              )}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.gridRowId}
              className={twMerge(
                "group border-b text-left transition-colors last:border-b-0 hover:bg-primary-10",
                theme.myVesselsTable.tailwind.tableBorder,
                theme.vesselDetails.info,
              )}
            >
              <td className="min-w-[180px] px-4 py-3 text-sm">{row.VENDOR}</td>
              <td className="min-w-[220px] px-4 py-3 text-sm">{row.PAINT}</td>
              <td className="min-w-[170px] px-4 py-3">
                <span
                  className={twMerge(
                    "inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold",
                    getPerformanceIndexColorClasses(row.PERFORMANCE_INDEX),
                  )}
                >
                  {row.PERFORMANCE_INDEX}
                </span>
              </td>
              <td className="w-[130px] px-4 py-3 text-sm">{row.HEMPEL_TIER}</td>
              <td className="min-w-[280px] px-4 py-3 text-sm">{row.COMMENTS || "—"}</td>
              <td className="min-w-[190px] whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                {formatAdminPaintSchemeUploadTimestamp(row.UPLOAD_TS)}
              </td>
              <td
                className={twMerge(
                  "sticky right-0 z-10 w-20 border-l px-4 py-3 text-right shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.45)] transition-colors group-hover:bg-primary-10",
                  theme.myVesselsTable.tailwind.tableBorder,
                  theme.uwhcAssistant.chatUi.panelBg,
                )}
              >
                <PaintSchemeRowActions disabled={disabled} onEdit={() => onEdit(row)} onDelete={() => onDelete(row)} />
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                No matching paints. Adjust the search or performance-index filter.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PaintSchemeRowActions({
  disabled,
  onEdit,
  onDelete,
}: {
  disabled: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const itemClass =
    "group flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors data-[headlessui-state=active]:bg-primary-10 data-[headlessui-state=active]:text-green dark:text-gray-200 dark:data-[headlessui-state=active]:bg-gray-700";

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        type="button"
        aria-label="Open row actions"
        disabled={disabled}
        className="border-greyscale-20 inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white text-lightGrey transition-colors hover:bg-primary-10 hover:text-green focus:outline-none focus:ring-2 focus:ring-green/30 disabled:cursor-not-allowed disabled:opacity-45 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
      >
        <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="border-greyscale-20 absolute right-0 z-30 mt-2 w-40 origin-top-right overflow-hidden rounded-md border bg-white py-1 shadow-lg focus:outline-none dark:border-gray-600 dark:bg-gray-800">
          <Menu.Item>
            <button type="button" onClick={onEdit} className={itemClass}>
              <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
              Edit
            </button>
          </Menu.Item>
          <Menu.Item>
            <button
              type="button"
              onClick={onDelete}
              className={twMerge(
                itemClass,
                "text-[#BF0002] data-[headlessui-state=active]:bg-[#FFF1F1] data-[headlessui-state=active]:text-[#BF0002]",
              )}
            >
              <TrashIcon className="h-4 w-4" aria-hidden="true" />
              Delete
            </button>
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

function SortableHeader({
  label,
  column,
  sortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  column: SortKey;
  sortKey: SortKey;
  sortDirection: "asc" | "desc";
  onSort: (key: SortKey) => void;
}) {
  return (
    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">
      <button
        type="button"
        onClick={() => onSort(column)}
        className="group inline-flex items-center gap-1.5 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-green/30"
      >
        {label}
        {sortKey === column ? (
          sortDirection === "asc" ? (
            <ChevronUpIcon className="h-3.5 w-3.5" />
          ) : (
            <ChevronDownIcon className="h-3.5 w-3.5" />
          )
        ) : (
          <ArrowsUpDownIcon className="h-3.5 w-3.5 opacity-35 transition group-hover:opacity-80" />
        )}
      </button>
    </th>
  );
}

function LoadingState() {
  return (
    <div className="mt-5 space-y-3" role="status" aria-label="Loading Paints Scheme records">
      <div className="h-16 animate-pulse rounded-xl bg-greyscale-10 dark:bg-gray-700" />
      {[0, 1, 2].map((row) => (
        <div key={row} className="h-12 animate-pulse rounded-lg bg-gray-50 dark:bg-gray-800" />
      ))}
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="mt-5 flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-red-200 bg-red-50/50 px-6 text-center dark:border-red-900 dark:bg-red-950/20">
      <p className="text-sm text-red-700 dark:text-red-200">Paints Scheme records could not be loaded.</p>
      <button type="button" onClick={onRetry} className="mt-4 font-semibold text-green underline underline-offset-2">
        Retry
      </button>
    </div>
  );
}

function DeletePaintSchemeDialog({
  open,
  row,
  deleting,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  row: AdminPaintSchemeGridRow | null;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onClose={() => !deleting && onCancel()} className="relative z-[1000]">
      <div className="fixed inset-0 bg-slate-950/55 backdrop-blur-[2px]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md overflow-hidden rounded-2xl border border-red-100 bg-white shadow-2xl dark:border-red-900/50 dark:bg-gray-800">
          <div className="p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-[#BF0002] dark:bg-red-950/50">
              <TrashIcon className="h-6 w-6" />
            </div>
            <Dialog.Title className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
              Delete Paint?
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
              {row ? `This permanently deletes ${getAdminPaintSchemeDisplayLabel(row)}. ` : ""}
              This action cannot be undone.
            </Dialog.Description>
          </div>
          <div className="border-greyscale-20 flex flex-col-reverse gap-3 border-t bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900/40 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onCancel}
              disabled={deleting}
              className="inline-flex h-11 items-center justify-center rounded-md border border-gray-300 bg-white px-5 text-sm font-semibold text-lightGrey dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={deleting}
              className="inline-flex h-11 items-center justify-center rounded-md bg-[#BF0002] px-5 text-sm font-semibold text-white shadow-sm hover:bg-[#9f0002] disabled:opacity-50"
            >
              {deleting && <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />}
              {deleting ? "Deleting..." : "Delete record"}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

export default AdminPaintSchemePanel;
